import { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { Stack } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text } from "@src/components";
import { Checkbox } from "@src/components/Checkbox";
import { getCameraKeyString, formatCameraLabel } from "@src/utils/key-parser";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { isWeb } from "@src/utils/web-helpers";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { PressableScale } from "pressto";
import { useManageAlarmResponse } from "./useManageAlarmResponse";
import { MonitoringBannerSection, MonitoringDialogs } from "@src/features/alarms/components/AlarmResponseShared";
import { useUpsellStatus } from "@visioni/a3s-upsell/app";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

export default function ManageAlarmResponseScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const { headerLeft } = useHeaderBackButton();

  const {
    siteCameras,
    siteId,
    isLoading,
    effectiveResponses,
    toggleResponse,
    allSirensOn,
    allStrobesOn,
    toggleAllSirens,
    toggleAllStrobes,
    hasChanges,
    isSaving,
    save,
  } = useManageAlarmResponse();

  const showUpsellBanner = useBooleanFlag(FEATURE_FLAGS.MONITORING_UPSELL_BANNER);
  const effectiveSiteId = siteId ?? "";
  const { isProductAlreadyActive, isLoading: isUpsellLoading } = useUpsellStatus({
    product: "monitoring",
    siteId: effectiveSiteId,
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);

  const handleUpgradeRequest = () => {
    setShowUpgradeModal(false);
    setShowRequestSentModal(true);
  };

  const headerTitle = useMemo(
    () => (
      <View flexDirection="row" justifyContent="space-between" alignItems="center" flex={1}>
        <View flex={1}>
          <Text textStyle="md/semibold" color="text" textAlign="center" style={localStyles.headerTitle}>
            Alarm response
          </Text>
        </View>
        <PressableScale
          onPress={save}
          style={[
            localStyles.saveButton,
            {
              opacity: !isSaving && hasChanges ? 1 : 0.5,
            },
          ]}
          enabled={!isSaving && hasChanges}
        >
          {isSaving ?
            <ActivityIndicator size="small" />
          : <Text textStyle="md/medium" color="white">
              Save
            </Text>
          }
        </PressableScale>
      </View>
    ),
    [isSaving, hasChanges, save],
  );

  return (
    <>
      <Stack.Screen options={{ title: "Alarm response", headerLeft, headerTitle: () => headerTitle }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoading && (
          <View style={{ maxWidth: 800 }}>
            {isWeb && (
              <View alignItems="flex-end" px="5xl" mb="xl">
                <Pressable
                  onPress={save}
                  style={{ opacity: !isSaving && hasChanges ? 1 : 0.5 }}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ?
                    <ActivityIndicator size="small" />
                  : <Text textStyle="md/semibold" color={hasChanges ? "navy" : "textMuted"}>
                      Save
                    </Text>
                  }
                </Pressable>
              </View>
            )}
            {showUpsellBanner && !isUpsellLoading && (
              <View px="5xl" mb="3xl">
                <MonitoringBannerSection
                  showMonitoring={isProductAlreadyActive}
                  siteId={effectiveSiteId}
                  onUpgrade={() => setShowUpgradeModal(true)}
                />
              </View>
            )}
            {/* Column headers */}
            <View style={localStyles.headerRow} px="5xl" gap="xxs">
              <View flex={1} />
              <View style={localStyles.columnHeader}>
                <View style={localStyles.textHeader}>
                  <Text textStyle="xs/medium" color="textMuted">
                    Sirens
                  </Text>
                </View>
                <Checkbox checked={allSirensOn} onToggle={toggleAllSirens} />
              </View>
              <View style={localStyles.columnHeader}>
                <View style={localStyles.textHeader}>
                  <Text textStyle="xs/medium" color="textMuted">
                    Strobes
                  </Text>
                </View>
                <Checkbox checked={allStrobesOn} onToggle={toggleAllStrobes} />
              </View>
            </View>

            {/* Camera rows */}
            {siteCameras.map((camera) => {
              const key = getCameraKeyString(camera);
              const state = effectiveResponses.get(key) ?? { sirens: "off", strobes: "off" };
              const displayName = camera.name || formatCameraLabel(key);
              const subtitleName = camera.name ? formatCameraLabel(key) : null;

              return (
                <View key={key} style={localStyles.row} px="5xl" gap="xxs">
                  <View flex={1}>
                    <Text textStyle="xxs/regular" color="text" numberOfLines={3}>
                      {toSentenceCase(displayName)}
                    </Text>
                    {subtitleName && (
                      <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                        {subtitleName}
                      </Text>
                    )}
                  </View>
                  <View style={localStyles.checkboxCell}>
                    <Checkbox checked={state.sirens === "on"} onToggle={() => toggleResponse(key, "sirens")} />
                  </View>
                  <View style={localStyles.checkboxCell}>
                    <Checkbox checked={state.strobes === "on"} onToggle={() => toggleResponse(key, "strobes")} />
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
      <MonitoringDialogs
        siteId={effectiveSiteId}
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        showRequestSentModal={showRequestSentModal}
        setShowRequestSentModal={setShowRequestSentModal}
        handleUpgradeRequest={handleUpgradeRequest}
      />
    </>
  );
}

const localStyles = StyleSheet.create((theme) => ({
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  columnHeader: {
    width: 72,
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  textHeader: {
    width: "100%",
    alignItems: "center",
    backgroundColor: theme.colors.borderCard,
    paddingVertical: theme.spacing.md,
    borderTopLeftRadius: theme.spacing.xs,
    borderTopRightRadius: theme.spacing.xs,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["7xl"],
  },
  checkboxCell: {
    width: 72,
    alignItems: "center",
  },
  saveButton: {
    paddingHorizontal: theme.spacing["5xl"],
    backgroundColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing["6xl"],
    marginRight: theme.spacing["5xl"],
    borderCurve: "continuous",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    marginLeft: theme.spacing["5xl"],
  },
}));
